import React, { createContext, useContext, useState, useEffect } from "react";

const UnitContext = createContext();

export const useUnits = () => useContext(UnitContext);

export const UnitProvider = ({ children }) => {
  const [units, setUnits] = useState(() => {
    try {
      const saved = localStorage.getItem("project_units");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("project_units", JSON.stringify(units));
  }, [units]);

  const createHistoryLog = (action, details) => ({
    date: new Date().toISOString(),
    action,
    details,
    user: "Admin"
  });

  // --- 🟢 SMART GENERATOR ENGINE (BUG FREE) ---
  const generateUnits = (projectId, config) => {
    // 1. Parse Inputs to Integers (Fixes "10" vs "2" string bug)
    const startFloor = parseInt(config.startFloor);
    const endFloor = parseInt(config.endFloor);
    const unitsPerFloor = parseInt(config.unitsPerFloor);
    const { namingFormat, tower, unitType, hasParking } = config;

    // 2. 🛡️ VALIDATION CHECKS (Stop before starting)
    if (isNaN(startFloor) || isNaN(endFloor)) {
        return { success: false, message: "Floors must be valid numbers." };
    }
    if (startFloor > endFloor) {
        return { success: false, message: `Start Floor (${startFloor}) cannot be higher than End Floor (${endFloor}).` };
    }
    if (unitsPerFloor < 1) {
        return { success: false, message: "Units per floor must be at least 1." };
    }
    if (!tower) {
        return { success: false, message: "Please specify a Tower/Wing name." };
    }

    const newUnits = [];
    let skippedCount = 0;

    // 3. Get existing units ONLY for this specific Tower & Project
    const existingInTower = units.filter(u => 
        String(u.projectId) === String(projectId) && 
        u.tower === tower
    ).map(u => u.unitNumber);

    // 4. GENERATION LOOP
    for (let f = startFloor; f <= endFloor; f++) {
      for (let u = 1; u <= unitsPerFloor; u++) {
        
        // Naming Logic (101, 102...)
        const floorPrefix = f.toString();
        const unitSuffix = u.toString().padStart(2, '0'); 
        const unitNumber = namingFormat === "F-U" ? `${floorPrefix}${unitSuffix}` : `${floorPrefix}0${u}`;

        // 🟢 SMART DUPLICATE CHECK
        // If "101" exists in THIS tower, skip it.
        if (existingInTower.includes(unitNumber)) {
            skippedCount++;
            continue; 
        }

        const parkingSlot = hasParking ? `P-${unitNumber}` : "None";

        newUnits.push({
          id: `UNIT-${projectId}-${tower}-${f}-${u}-${Date.now()}-${Math.random()}`,
          projectId,
          tower: tower,
          floor: f,
          unitNumber: unitNumber,
          type: unitType || "2BHK", 
          parking: parkingSlot,
          status: "Available", 
          area: unitType === "3BHK" ? 1500 : unitType === "2BHK" ? 1200 : 800,
          price: 0,
          furnishing: "Unfurnished",
          owner: null,
          history: [ createHistoryLog("Created", `Unit generated in ${tower}`) ]
        });
      }
    }

    // 5. FINAL REPORT
    if (newUnits.length > 0) {
        setUnits(prev => [...prev, ...newUnits]);
        return { 
            success: true, 
            count: newUnits.length, 
            skipped: skippedCount,
            message: `Success! Created ${newUnits.length} units.` 
        };
    } else if (skippedCount > 0) {
        // Case: Logic was correct, but they all already existed
        return { success: false, message: `No units created. All ${skippedCount} units already exist in ${tower}.` };
    } else {
        // Case: Unknown error or loop didn't run
        return { success: false, message: "No units generated. Check your inputs." };
    }
  };

  // --- CRUD ACTIONS ---
  const addUnit = (unitData) => {
    // Check global duplicate for single add
    const exists = units.some(u => 
        String(u.projectId) === String(unitData.projectId) && 
        u.tower === unitData.tower && 
        u.unitNumber === unitData.unitNumber
    );

    if (exists) {
        return { success: false, message: `Unit ${unitData.unitNumber} already exists in ${unitData.tower}!` };
    }

    setUnits(prev => [...prev, { 
        ...unitData, 
        id: `MANUAL-${Date.now()}`, 
        status: "Available",
        history: [ createHistoryLog("Created", "Manually added") ]
    }]);
    return { success: true };
  };

  const updateUnit = (id, updates, changeMessage = "Updated details") => {
    setUnits(prev => prev.map(u => {
        if (String(u.id) === String(id)) {
            return { 
                ...u, 
                ...updates,
                history: [ createHistoryLog("Update", changeMessage), ...(u.history || []) ] 
            };
        }
        return u;
    }));
  };

  const deleteUnit = (id) => {
    setUnits(prev => prev.filter(u => String(u.id) !== String(id)));
  };

  const updateUnitStatus = (id, status, ownerDetails = null) => {
    setUnits(prev => prev.map(u => {
      if (String(u.id) === String(id)) {
        const historyMsg = ownerDetails ? `Assigned to ${ownerDetails.name} (${status})` : `Status: ${status}`;
        return { 
          ...u, 
          status, 
          owner: ownerDetails ? { ...u.owner, ...ownerDetails } : (status === "Available" ? null : u.owner),
          history: [ createHistoryLog("Status Change", historyMsg), ...(u.history || []) ]
        };
      }
      return u;
    }));
  };

  // --- GETTERS ---
  const getProjectUnits = (projectId) => units.filter(u => String(u.projectId) === String(projectId));
  
  const getTowers = (projectId) => {
    const projectUnits = getProjectUnits(projectId);
    const towers = [...new Set(projectUnits.map(u => u.tower))];
    return towers.sort();
  };

  const getFloorsByTower = (projectId, towerName) => {
    const projectUnits = getProjectUnits(projectId).filter(u => u.tower === towerName);
    const floors = [...new Set(projectUnits.map(u => u.floor))];
    return floors.sort((a, b) => b - a); 
  };

  return (
    <UnitContext.Provider value={{ 
      units, generateUnits, addUnit, updateUnit, deleteUnit, 
      updateUnitStatus, getProjectUnits, getTowers, getFloorsByTower 
    }}>
      {children}
    </UnitContext.Provider>
  );
};